import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import captureException from 'src/common/utils/captureException';
import getRedirectDestination from './getRedirectDestination';
import isApiResponseError from './isApiResponseError';

function send(context: GetServerSidePropsContext, status: number, statusText: string) {
  context.res.writeHead(status, statusText);
  context.res.write(statusText);
  context.res.end();
  return {
    props: {
      status,
      errorPage: true,
    },
  };
}

type CustomErrorProps = {
  status?: number;
  errorPage?: boolean;
};

/**
 * This is a higher order function to wrap around getServerSideProps to handle 4xx and 5xx
 * API statuses.
 *
 * Handles 400, 401, 404, 429, 500, 502, 504 statuses.
 */
function handleApiErrors<
  Props extends { [key: string]: any },
  Params extends ParsedUrlQuery = {},
>(
  handler: GetServerSideProps<Props, Params>,
): GetServerSideProps<Props | CustomErrorProps, Params> {
  return async (context) => {
    try {
      return await handler(context);
    } catch (err: unknown) {
      if (isApiResponseError(err, 401)) {
        const destination = await getRedirectDestination(err);
        if (destination) {
          return {
            redirect: {
              destination,
              permanent: false,
            },
          };
        }
      } else if (isApiResponseError(err, 400)) {
        return send(context, 400, 'Bad Request');
      } else if (isApiResponseError(err, 429)) {
        return send(context, 429, 'Too Many Requests');
      } else if (isApiResponseError(err, 500)) {
        return send(context, 500, 'Internal Server Error');
      } else if ([502, 504].some((status) => isApiResponseError(err, status))) {
        // Don't capture sentry errors for 502 and 504
      } else if (isApiResponseError(err, 404)) {
        // Don't capture sentry errors for 404
        return {
          notFound: true,
        };
      } else {
        // Only capture exceptions for API responses that are not handled
        captureException(err);
      }
    }

    return send(context, 500, 'Internal Server Error');
  };
}

/**
 * Provides control flow by throwing certain 4xx and 5xx errors to allow centralized error
 * instead of each page needing to implement the same error handling.
 *
 * Any API errors that a page would like to handle themselves should be handled before this
 * function gets called.
 *
 * The thrown ApiResponseError should be handled by handleApiErrors
 *
 * Throws if the errors are 400, 401, 404, 429, 500, 502, 504.
 *
 * Returns false if none of the status codes are a match.
 */
export const throwApiErrors = (err: unknown): boolean => {
  if ([400, 401, 404, 429, 500, 502, 504].some((status) => isApiResponseError(err, status))) {
    // 400 - rethrow bad request errors.
    // 401 - rethrow the error for banned/suspended users so that we can redirect them.
    // 404 - rethrow not found errors.
    // 429 - rethrow the error for rate limited users.
    // 502 - rethrow bad gateway errors.
    // 504 - rethrow timeout errors.
    throw err;
  }

  return false;
};

export default handleApiErrors;
